import { useState } from 'react';
import { Link, Navigate } from 'react-router';
import { useLogin, useSession } from '../auth/session';
import { AuthLayout } from './AuthLayout';
import { Banner, Button, Field, TextInput } from '../components/ui';
import { errorCode, errorMessage } from '../api/problems';

export function LoginPage() {
  const { data: user } = useSession();
  const login = useLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  if (user) {
    return <Navigate to="/" replace />;
  }

  const locked = errorCode(login.error) === 'AccountLockedOut';

  return (
    <AuthLayout
      title="Sign in"
      subtitle="Your drawer is private. Everything is behind login."
      footer={
        <>
          No account yet?{' '}
          <Link to="/register" className="text-accent hover:underline">
            Create one
          </Link>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          login.mutate({ email, password, rememberMe });
        }}
      >
        {login.isError && (
          <Banner tone="error">
            {locked
              ? 'Too many failed attempts. Try again shortly, or reset your password.'
              : errorMessage(login.error, 'That email and password combination was not recognised.')}
          </Banner>
        )}

        <Field label="Email">
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>

        <Field label="Password">
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </Field>

        <label className="flex items-center gap-2 text-sm text-text-muted">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(event) => setRememberMe(event.target.checked)}
            className="accent-[var(--accent)]"
          />
          Keep me signed in
        </label>

        <Button type="submit" variant="primary" disabled={login.isPending}>
          {login.isPending ? 'Signing in…' : 'Sign in'}
        </Button>

        <Link to="/forgot-password" className="text-sm text-text-muted hover:text-text">
          Forgotten your password?
        </Link>
      </form>
    </AuthLayout>
  );
}
